import React, { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import useIsMobileDevice from '../../hooks/use-is-mobile-device';
import Overlay from '../overlay/overlay';
import { OverlayTriggerContext } from './overlay-trigger-context';

export type PlacementType =
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';

export type TriggerType = 'hover' | 'click' | 'focus' | Array<'hover' | 'click' | 'focus'>;

type ElementEventsType = {
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onClick?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
};

export type ChildElementType = React.ReactElement<ElementEventsType>;

type RenderPropChildType = (
    props: {
        ref: React.Ref<HTMLElement>;
    } & ElementEventsType
) => React.ReactElement;

type OverlayTriggerPropsType = {
    children: ChildElementType | RenderPropChildType;
    overlay: React.ReactElement;
    placement?: PlacementType;
    fallbackPlacements?: PlacementType[];
    trigger?: TriggerType;
    show?: boolean;
    defaultShow?: boolean;
    onToggle?: (show: boolean) => void;
    delay?: number | { show: number; hide: number };
    container?: HTMLElement | (() => HTMLElement | null) | null;
    rootClose?: boolean;
    rootCloseEvent?: 'click' | 'mousedown';
};

const INTERACTABLE_HIDE_DELAY = 150;

export const OverlayTrigger: React.FC<OverlayTriggerPropsType> = (props) => {
    const triggers = Array.isArray(props.trigger) ? props.trigger : [props.trigger ?? 'hover'];
    // Touch devices have no cursor, so hover never fires — open hover overlays on tap instead,
    // otherwise a hover-only tooltip is unreachable on mobile.
    const isTouchDevice = useIsMobileDevice();
    const shouldTriggerOnHover = triggers.includes('hover') && !isTouchDevice;
    const shouldTriggerOnClick = triggers.includes('click') || (triggers.includes('hover') && isTouchDevice);

    const {
        children,
        overlay,
        placement = 'top',
        fallbackPlacements,
        show: controlledShow,
        defaultShow = false,
        onToggle,
        delay,
        container,
        rootClose = shouldTriggerOnClick,
        rootCloseEvent = 'click'
    } = props;

    const [internalShow, setInternalShow] = useState(defaultShow);
    const childRef = useRef<HTMLElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const openedByClickRef = useRef(false);
    const isInteractableRef = useRef(false);

    const isControlled = controlledShow !== undefined;
    const show = isControlled ? controlledShow : internalShow;

    const setInteractable = (value: boolean) => {
        isInteractableRef.current = value;
    };

    const updateShow = (nextShow: boolean) => {
        if (!isControlled) {
            setInternalShow(nextShow);
        }
        onToggle?.(nextShow);
    };

    const resolveDelay = (type: 'show' | 'hide'): number => {
        if (typeof delay === 'number') {
            return delay;
        }

        if (delay?.[type] !== undefined) {
            return delay[type];
        }

        if (type === 'hide') {
            // Delay hide so the user's mouse can travel from trigger into an interactable tooltip without it closing.
            const tooltipOpenedByHover = isInteractableRef.current && shouldTriggerOnHover && !openedByClickRef.current;

            return tooltipOpenedByHover ? INTERACTABLE_HIDE_DELAY : 0;
        }

        return 0;
    };

    const handleShow = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const delayShow = resolveDelay('show');

        if (delayShow > 0) {
            timeoutRef.current = setTimeout(() => updateShow(true), delayShow);
        } else {
            updateShow(true);
        }
    };

    const handleHide = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const delayHide = resolveDelay('hide');

        if (delayHide > 0) {
            timeoutRef.current = setTimeout(() => updateShow(false), delayHide);
        } else {
            updateShow(false);
        }
    };

    const handleHideIfTriggeredByHover = () => {
        if (!openedByClickRef.current) {
            handleHide();
        }
    };

    const handleToggle = () => {
        if (show) {
            handleHide();
            openedByClickRef.current = false;
        } else {
            openedByClickRef.current = true;
            handleShow();
        }
    };

    const isEventFromTrigger = (event: React.SyntheticEvent) =>
        !childRef.current || childRef.current.contains(event.target as Node);

    const handleTriggerShow = (event: React.SyntheticEvent) => {
        if (isEventFromTrigger(event)) {
            handleShow();
        }
    };

    const handleTriggerHide = (event: React.SyntheticEvent) => {
        if (isEventFromTrigger(event)) {
            handleHide();
        }
    };

    const handleTriggerToggle = (event: React.SyntheticEvent) => {
        if (isEventFromTrigger(event)) {
            handleToggle();
        }
    };

    useEffect(() => {
        const triggerNode = childRef.current;

        if (!shouldTriggerOnHover || !triggerNode) {
            return;
        }

        triggerNode.addEventListener('mouseenter', handleShow);
        triggerNode.addEventListener('mouseleave', handleHideIfTriggeredByHover);

        return () => {
            triggerNode.removeEventListener('mouseenter', handleShow);
            triggerNode.removeEventListener('mouseleave', handleHideIfTriggeredByHover);
        };
    });

    const childProps: {
        ref: React.Ref<HTMLElement>;
    } & ElementEventsType = {
        ref: childRef
    };

    const isRenderProp = typeof children === 'function';

    if (!isRenderProp) {
        const originalProps = children.props;

        if (shouldTriggerOnClick) {
            childProps.onClick = (event: React.MouseEvent) => {
                if (originalProps.onClick) {
                    originalProps.onClick(event);
                }
                handleTriggerToggle(event);
            };
        }

        if (triggers.includes('focus')) {
            childProps.onFocus = (event: React.FocusEvent) => {
                if (originalProps.onFocus) {
                    originalProps.onFocus(event);
                }
                handleTriggerShow(event);
            };
            childProps.onBlur = (event: React.FocusEvent) => {
                if (originalProps.onBlur) {
                    originalProps.onBlur(event);
                }
                handleTriggerHide(event);
            };
        }
    } else {
        if (shouldTriggerOnClick) {
            childProps.onClick = handleTriggerToggle;
        }

        if (triggers.includes('focus')) {
            childProps.onFocus = handleTriggerShow;
            childProps.onBlur = handleTriggerHide;
        }
    }

    return (
        <OverlayTriggerContext.Provider
            value={{ handleShow, handleHide, handleHideIfTriggeredByHover, shouldTriggerOnHover, setInteractable }}
        >
            {isRenderProp ? children(childProps) : isValidElement(children) && cloneElement(children, childProps)}
            <Overlay
                show={show}
                target={childRef.current}
                placement={placement}
                fallbackPlacements={fallbackPlacements}
                container={container}
                onHide={handleHide}
                rootClose={rootClose}
                rootCloseEvent={rootCloseEvent}
            >
                {overlay}
            </Overlay>
        </OverlayTriggerContext.Provider>
    );
};
